import React, {useEffect, useRef, useState} from "react";
import moment from "moment";
import _ from "lodash";
import { decode } from 'html-entities';
import {CircularProgress, Typography} from "@material-ui/core";
import {Badge, Col} from "react-bootstrap";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faMapMarkerAlt, faClock} from '@fortawesome/free-solid-svg-icons';

import {useOffice} from "../../../../hooks";
import QuickInfoPanel from "../../../../assets/js/layout/extended/info-panel";
import AnimateLoading from "../../../_partials/controls/AnimateLoading";
import Loading from "./Loading";

export default function OfficeInfo({officeId}) {
    const {isLoading, error, data, isFetching} = useOffice(officeId);
    const [office, setOffice] = useState(null);
    const [workingProgram, setWorkingProgram] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [clock, setClock] = useState(null);
    const [day, setDay] = useState('Wednesday');
    const scrollInfo = useRef(null);

    useEffect(() => {
        if (data && typeof data.data.data !== 'undefined') {
            setOffice(data.data.data.office || null);
            setWorkingProgram(data.data.data.working_program || []);
        }
    }, [data]);

    useEffect(() => {
        let timerInterval = null;

        function tikTok() {
            if (office.timezone && workingProgram) {
                setClock(moment().utcOffset(office.timezone ? office.timezone.gmt : null).format('hh:mm:ss'));
                setDay(moment().utcOffset(office.timezone ? office.timezone.gmt : null).format('dddd'));

                const curentDay = moment().utcOffset(office.timezone.gmt).format('E');
                const dayOfWeek = _.find(workingProgram, {"day": parseInt(curentDay)});

                if (dayOfWeek) {
                    let _start = new Date(moment(dayOfWeek.startProgramTime, "HH:mm")).getTime();
                    let _end = new Date(moment(dayOfWeek.endProgramTime, "HH:mm")).getTime();
                    let _now = new Date(
                        moment(moment().utcOffset(office.timezone.gmt).format('HH:mm'), "HH:mm")).getTime();

                    if (_now >= _start && _now <= _end) {
                        setIsOpen(true);
                    } else {
                        setIsOpen(false);
                    }
                }
            } else {
                setClock(moment().format('hh:mm:ss'));
                setDay(moment().format('dddd'));
            }
        }

        if (scrollInfo.current) {
            QuickInfoPanel.init(scrollInfo.current.id)
        }

        if (office) {
            tikTok();
            timerInterval = setInterval(tikTok, 1000);
        }

        return () => {
            if (timerInterval) clearInterval(timerInterval);
        }
    }, [office]);

    function splitWorkProgram(workProgram) {
        if (!workProgram) return null;

        return workProgram.split("\n").map((day, index) => {
            return <p key={index}>{day}</p>;
        });
    }

    function displayTimeZone(timeZone) {
        return <div>{decode(timeZone.timezone)}</div>;
    }

    if (!office || isLoading) return <Loading/>;

    return (
        <div className="offcanvas-content px-10" id="company-info-panel" ref={scrollInfo}>
            <div className="card card-custom wave mb-8 mb-lg-0 mx-n10 rounded-0 px-4">
                <div className="card-body p-4">
                    <div className="d-flex py-5">
                        <div className="mr-6">
                            <div className="svg-icon svg-icon-success svg-icon-4x mb-4">
                                <FontAwesomeIcon icon={faMapMarkerAlt} size={`4x`}/>
                            </div>
                            {
                                isOpen ? <Badge variant="success">Open</Badge>
                                    :
                                    <Badge variant="danger">Closed</Badge>
                            }
                        </div>

                        <div>
                            <div className="text-dark text-hover-primary font-weight-bold font-size-h4 mb-1">
                                {office.office_name}
                            </div>
                            <div className="text-dark-75">
                                <div className="d-flex justify-content-between">
                                    <div className="font-size-h2">
                                        <FontAwesomeIcon icon={faClock}/> {clock}
                                    </div>
                                    <span className="mt-3 ml-3 text-primary">{day}</span>
                                </div>

                                {office.timezone && displayTimeZone(office.timezone)}
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <div className="separator separator-dashed mt-8 mb-5"></div>

            <div>
                <div className="d-flex justify-content-between mb-2">
                    <span className="font-weight-bold mr-4">Address:</span>
                    <span className="text-muted text-hover-primary">{office.address}</span>
                </div>

                <div className="d-flex justify-content-between mb-2">
                    <span className="font-weight-bold mr-4">Phone:</span>
                    <span className="text-muted text-hover-primary">{office.phone}</span>
                </div>

                <div className="d-flex justify-content-between mb-2">
                    <span className="font-weight-bold mr-4">Email:</span>
                    <span className="text-muted text-hover-primary">{office.email_address}</span>
                </div>

                <div className="d-flex justify-content-between mb-2">
                    <span className="font-weight-bold mr-4">ZIP:</span>
                    <span className="text-muted text-hover-primary">{office.zip}</span>
                </div>

                <div className="d-flex justify-content-between mb-2">
                    <span className="font-weight-bold mr-4">Region:</span>
                    <span
                        className="text-muted text-hover-primary">{office.region ? office.region.region_name : ''}</span>
                </div>

                <div className="d-flex justify-content-between mb-2">
                    <span className="font-weight-bold mr-4">District:</span>
                    <span className="text-muted text-hover-primary">{office.district_id}</span>
                </div>

                <div className="d-flex justify-content-between mb-2">
                    <span className="font-weight-bold mr-4">State:</span>
                    <span className="text-muted text-hover-primary">{office.state}</span>
                </div>

                <div className="d-flex justify-content-between mb-2">
                    <span className="font-weight-bold mr-4">City:</span>
                    <span className="text-muted text-hover-primary">{office.city}</span>
                </div>

                <div className="d-flex justify-content-between mb-2">
                    <span className="font-weight-bold mr-4">Manager:</span>
                    <span className="text-muted text-hover-primary">{office.manager}</span>
                </div>

                <div className="d-flex justify-content-between mb-2">
                    <span className="font-weight-bold mr-4">Latitude:</span>
                    <span className="text-muted text-hover-primary">{office.latitude}</span>
                </div>

                <div className="d-flex justify-content-between mb-2">
                    <span className="font-weight-bold mr-4">Longitude:</span>
                    <span className="text-muted text-hover-primary">{office.longitude}</span>
                </div>

                <div className="separator separator-dashed mt-8 mb-5"></div>

                <div className="mb-2">
                    <div className="font-weight-bold mb-4">Work program:</div>
                    <span className="text-muted">{splitWorkProgram(office.work_program)}</span>
                </div>
            </div>
        </div>
    );
}
